import {
  Box,
  Button,
  CheckboxOption,
  Input,
  Select,
} from '@nerdwallet/currency';
import Accordion from '@nerdwallet/react-accordion';
import Trash from '@nerdwallet/react-icon/gizmo/Trash';
import camelCase from 'lodash/camelCase';
import { Fragment } from 'react';
import {
  Data,
  TABLE_STYLES,
  TableColumn,
  TableRow,
  TableStylesType,
} from '~/app/components/fixed-first-column-table/types';
import { useMediaQuery } from '~/app/lib/use-media-query';

import BaseFixedFirstColumnTable from '~/app/components/fixed-first-column-table/base-fixed-first-column-table';
import {
  DynamicTextComponent,
  DynamicTextForm,
} from '../dynamic-text/dynamic-text-component';
import { useDynamicFixedFirstColumnTable } from './use-dynamic-fixed-first-column-table';

import styles from './dynamic-fixed-first-column-table.module.scss';

type DynamicFixedFirstColumnTableProps = {
  tableSettings?: {
    centeredInParent: boolean;
    tableStyle: TableStylesType;
    transposed: boolean;
    hideHeader: boolean;
    striped: boolean;
  };
  headers?: TableColumn[];
  data?: TableRow[];
};

export const DynamicFixedFirstColumnTable = ({
  tableSettings = {
    centeredInParent: false,
    tableStyle: '',
    transposed: false,
    hideHeader: false,
    striped: false,
  },
  headers = [],
  data = [],
}: DynamicFixedFirstColumnTableProps) => {
  const size = useMediaQuery();
  const isMobile = size === 'sm';

  const { centeredInParent, tableStyle, transposed, hideHeader, striped } =
    tableSettings;

  const tableData: TableRow[] = data.map((row) => {
    const mappedRow: Record<string, any> = {};
    // Map each attribute to camelCase key with its value
    if (row.attributes) {
      row.attributes.forEach((attribute: any) => {
        const key = camelCase(attribute.header);
        mappedRow[key] = attribute.value;
      });
    }

    if (row.highlight !== undefined) {
      mappedRow.highlight = row.highlight;
    }

    return mappedRow as TableRow;
  });

  const renderCell = (column: TableColumn, row: TableRow) => {
    const value = row[column.key];
    if (
      transposed &&
      tableStyle === 'styled-light-green' &&
      column.key === 'originalHeader'
    ) {
      return value.text;
    }

    if (isMobile && tableStyle === 'styled-light-green') {
      value.groupButton.left = true;
      value.groupButton.center = false;
      value.groupButton.right = false;
    }

    return <DynamicTextComponent {...value} />;
  };

  const enableHighlighting = ['styled-light-green'].includes(tableStyle);

  return (
    <BaseFixedFirstColumnTable
      columns={headers || []}
      tableData={tableData || []}
      tableStyle={tableStyle}
      centeredInParent={centeredInParent}
      enableHighlighting={enableHighlighting}
      hideHeader={hideHeader}
      striped={striped}
      transposed={transposed}
      renderCell={renderCell}
    />
  );
};

export const DynamicFixedFirstColumnTableForm = ({
  fieldPath = '',
}: {
  fieldPath?: string;
}) => {
  const {
    tableSettings,
    headers,
    data,
    handleCenteredInParentChange,
    handleHideHeaderChange,
    handleStripedChange,
    handleTableStyleChange,
    handleTransposedChange,
    handleAddHeader,
    handleDeleteHeader,
    handleHeaderChange,
    handleAddData,
    handleDeleteData,
    handleHighlightChange,
  } = useDynamicFixedFirstColumnTable({
    fieldPath,
  });

  const { centeredInParent, hideHeader, striped, tableStyle, transposed } =
    tableSettings;

  return (
    <Box paddingY={2}>
      <Box paddingY={2}>
        <CheckboxOption
          label="Centered in Parent?"
          checked={centeredInParent}
          onChange={handleCenteredInParentChange}
        />
      </Box>
      <Box paddingY={2}>
        <div className={styles.accordionWrapper}>
          <Accordion
            items={[
              {
                id: 'table-settings',
                title: (
                  <Box className={styles.accordionHeaderContainer}>
                    <span>Table Settings</span>
                  </Box>
                ),
                children: (
                  <Fragment>
                    <Box padding={2}>
                      <Select
                        label="Table Style"
                        value={tableStyle}
                        onChange={handleTableStyleChange}
                        options={TABLE_STYLES}
                      />
                    </Box>
                    <Box padding={2}>
                      <CheckboxOption
                        label="Transposed"
                        checked={transposed}
                        onChange={handleTransposedChange}
                      />
                    </Box>
                    {tableStyle === '' && (
                      <Fragment>
                        <Box padding={2}>
                          <CheckboxOption
                            label="Hide Header"
                            checked={hideHeader}
                            onChange={handleHideHeaderChange}
                          />
                        </Box>
                        <Box padding={2}>
                          <CheckboxOption
                            label="Striped"
                            checked={striped}
                            onChange={handleStripedChange}
                          />
                        </Box>
                      </Fragment>
                    )}
                  </Fragment>
                ),
              },
            ]}
          />
        </div>
        <div className={styles.accordionWrapper}>
          <Accordion
            items={[
              {
                id: 'column-settings',
                title: (
                  <Box className={styles.accordionHeaderContainer}>
                    <span>Header Settings</span>
                  </Box>
                ),
                children: (
                  <Box paddingY={2}>
                    <p>Headers</p>
                    <Box paddingY={2}>
                      {headers.map((header, index) => (
                        <div
                          className={styles.accordionWrapper}
                          key={header.key}
                        >
                          <Accordion
                            key={header.key}
                            items={[
                              {
                                title: (
                                  <Box className={styles.itemHeaderContainer}>
                                    <span>{header.header}</span>
                                    <Trash
                                      onClick={() => handleDeleteHeader(index)}
                                      size={15}
                                      title="delete this header"
                                      className="trash"
                                    />
                                  </Box>
                                ),
                                children: (
                                  <Box padding={2} key={header.key}>
                                    <Input
                                      label="Header"
                                      value={header.header}
                                      onChange={(e) =>
                                        handleHeaderChange(
                                          index,
                                          e.target.value
                                        )
                                      }
                                    />
                                  </Box>
                                ),
                                reducedPadding: true,
                              },
                            ]}
                          />
                        </div>
                      ))}
                    </Box>

                    <Button
                      variant="secondary"
                      block
                      onClick={handleAddHeader}
                      fullWidth
                    >
                      Add header
                    </Button>
                  </Box>
                ),
              },
            ]}
          />
        </div>
        <div className={styles.accordionWrapper}>
          <Accordion
            items={[
              {
                id: 'data-settings',
                title: (
                  <Box className={styles.accordionHeaderContainer}>
                    <span>Data</span>
                  </Box>
                ),
                children: (
                  <Box paddingY={2}>
                    <p>Data</p>
                    <Box paddingY={2}>
                      {data.map((row: Data, index: number) => (
                        <div className={styles.accordionWrapper} key={row.id}>
                          <Accordion
                            key={row.id}
                            items={[
                              {
                                title: (
                                  <Box className={styles.itemHeaderContainer}>
                                    <span>{`Data ${index + 1}`}</span>
                                    <Trash
                                      onClick={() => handleDeleteData(index)}
                                      size={15}
                                      title="delete this element"
                                      className="trash"
                                    />
                                  </Box>
                                ),
                                children: (
                                  <Box padding={2}>
                                    <Box padding={2}>
                                      <CheckboxOption
                                        label="Highlight"
                                        checked={row.highlight}
                                        onChange={() =>
                                          handleHighlightChange(index)
                                        }
                                      />
                                    </Box>
                                    {row.attributes.map((attribute, idx) => (
                                      <div
                                        className={styles.accordionWrapper}
                                        key={attribute.header}
                                      >
                                        <Accordion
                                          key={attribute.header}
                                          items={[
                                            {
                                              title: (
                                                <Box
                                                  className={
                                                    styles.itemHeaderContainer
                                                  }
                                                >
                                                  <span>
                                                    {attribute.header}
                                                  </span>
                                                </Box>
                                              ),
                                              children: (
                                                <Box
                                                  padding={2}
                                                  key={attribute.header}
                                                >
                                                  <DynamicTextForm
                                                    fieldPath={
                                                      fieldPath
                                                        ? `${fieldPath}.data.${index}.attributes.${idx}.value`
                                                        : `data.${index}.attributes.${idx}.value`
                                                    }
                                                  />
                                                </Box>
                                              ),
                                              reducedPadding: true,
                                            },
                                          ]}
                                        />
                                      </div>
                                    ))}
                                  </Box>
                                ),
                                reducedPadding: true,
                              },
                            ]}
                          />
                        </div>
                      ))}
                    </Box>

                    <Button
                      variant="secondary"
                      block
                      onClick={handleAddData}
                      fullWidth
                    >
                      Add data
                    </Button>
                  </Box>
                ),
              },
            ]}
          />
        </div>
      </Box>
    </Box>
  );
};
